import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button, Modal, Badge, Row, Col } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../../components/auth/useAuth'
import { getProductHistory } from '../../actions/productHistoryActions'
import { CustomDataTable } from '../../components/design/Datatables/CustomDataTable'
import Loader from '../../components/loaders/Loader'

const formatItem = (item) => {
    if (typeof item !== 'object' || item === null) return String(item)
    if (item.label) return item.label
    if (item.name) return item.name
    if (item.url) return item.url
    if (item.key) return item.key
    return JSON.stringify(item)
}

const formatDiffItem = (item) => {
    if (typeof item !== 'object' || item === null) return String(item)
    if (item.label && item.value !== undefined) {
        const val = item.value ?? ''
        const unit = item.unit ? ` ${item.unit}` : ''
        return `${item.label}: ${val}${unit}`
    }
    if (item.name) return item.name
    if (item.url) return item.url
    if (item.key) return `${item.key}: ${item.value ?? ''}`
    return JSON.stringify(item)
}

const diffArrays = (oldArr = [], newArr = []) => {
    const getId = (item) => item?._id || item?.key || item?.url || JSON.stringify(item)
    const oldMap = new Map(oldArr.map(item => [getId(item), item]))
    const newMap = new Map(newArr.map(item => [getId(item), item]))
    const changes = []

    for (const [id, oldItem] of oldMap) {
        if (!newMap.has(id)) {
            changes.push({ type: 'removed', oldItem })
        } else {
            const newItem = newMap.get(id)
            if (JSON.stringify(oldItem) !== JSON.stringify(newItem)) {
                changes.push({ type: 'modified', oldItem, newItem })
            }
        }
    }

    for (const [id, newItem] of newMap) {
        if (!oldMap.has(id)) {
            changes.push({ type: 'added', newItem })
        }
    }

    return changes
}

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return '-'
    }

    if (Array.isArray(value)) {
        if (value.length === 0) return '-'
        const allStrings = value.every(item => typeof item === 'string')
        if (allStrings) {
            return value.join(', ')
        }
        return value.map(item => formatItem(item)).join(', ')
    }

    if (typeof value === 'object') {
        return formatItem(value)
    }

    return String(value)
}

const getUserLabel = (user) => {
    return user?.email || user?.username || user?.name || '-'
}

export default function ProductHistory() {
    const { t } = useTranslation()
    const { id } = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const {clientauth} = useAuth()

    const {history, loading} = useSelector(state=>state.productHistory)

    const [modalShow, setModalShow] = useState(false)
    const [selectedVersion, setSelectedVersion] = useState(null)

    useEffect(() => {
        if(clientauth && id){
            dispatch(getProductHistory(id))
        }
    }, [dispatch, clientauth, id])

    const viewVersion = (version) => {
        setSelectedVersion(version)
        setModalShow(true)
    }

    const columnsHistory = [
        {
            name: t('admin.productHistory.date'),
            minWidth: '140px',
            cell: version =>
                <div onClick={() => viewVersion(version)} className='cursor'>{new Date(version.createdAt).toLocaleString()}</div>,
            selector: (version) => version.createdAt,
            sortable: true,
        },{
            name: t('admin.productHistory.action'),
            minWidth: '90px',
            cell: version =>
                <div onClick={() => viewVersion(version)} className='cursor'>{t('admin.productHistory.'+version.action)}</div>,
            selector: (version) => version.action,
            sortable: true,
        },{
            name: t('admin.productHistory.user'),
            minWidth: '130px',
            cell: version =>
                <div onClick={() => viewVersion(version)} className='cursor'>{getUserLabel(version.user)}</div>,
            selector: (version) => getUserLabel(version.user),
            sortable: true,
            hide: 'sm',
        },{
            name: t('admin.productHistory.changes'),
            minWidth: '70px',
            center: true,
            cell: version =>
                <Badge bg="info">{version.changes?.length || 0}</Badge>,
            selector: (version) => version.changes?.length || 0,
            sortable: true,
            hide: 'sm',
        },
    ]

    return (
        <>
            <div className="toolbar">
                <div className="toolbar-left">
                    <Button variant="secondary" onClick={() => navigate(-1)}>
                        <i className="fas fa-arrow-left"></i>{t('global.return')}
                    </Button>
                    <h4 className="mb-0">{t('admin.productHistory.title')}</h4>
                </div>
            </div>

            {loading ? (
                <Loader />
            ) : (
                <div className="card-simple">
                    <CustomDataTable
                        columns={columnsHistory}
                        data={history}
                        pagination={false}
                        exportable={true}
                        printable={true}
                        filter={false}
                        onRowClicked={(row) => viewVersion(row)}
                    />
                </div>
            )}
            <Modal show={modalShow} onHide={() => setModalShow(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('admin.productHistory.versionDetails')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedVersion && (
                        <Row>
                            <Col xs={12} lg={6} className='my-3'>
                                <p><strong>{t('admin.productHistory.date')}:</strong> {new Date(selectedVersion.createdAt).toLocaleString()}</p>
                                <p><strong>{t('admin.productHistory.action')}:</strong> {t('admin.productHistory.'+selectedVersion.action)}</p>
                                <p><strong>{t('admin.productHistory.user')}:</strong> {getUserLabel(selectedVersion.user)}</p>
                            </Col>
                            <Col xs={12} lg={6} className='my-3'>
                                <h5>{t('admin.productHistory.changes')}:</h5>
                                <ul className="list-unstyled">
                                    {selectedVersion.changes?.map((change, index) => {
                                        const bothArraysOfObjects =
                                            Array.isArray(change.oldValue) && Array.isArray(change.newValue) &&
                                            (change.oldValue.length === 0 || typeof change.oldValue[0] === 'object') &&
                                            (change.newValue.length === 0 || typeof change.newValue[0] === 'object')
                                        const diffs = bothArraysOfObjects ? diffArrays(change.oldValue, change.newValue) : null

                                        return (
                                            <li key={index} className="my-2">
                                                <strong>{t('admin.productHistory.'+change.field)}:</strong>
                                                {diffs && diffs.length > 0 ? (
                                                    <div className="ms-3">
                                                        {diffs.map((d, i) => (
                                                            <div key={i}>
                                                                {d.type === 'added' && (
                                                                    <div className="text-success"><em>{t('admin.productHistory.nothing')}</em> → {formatDiffItem(d.newItem)}</div>
                                                                        )}
                                                                        {d.type === 'removed' && (
                                                                            <div className="text-danger">{formatDiffItem(d.oldItem)} → <em>{t('admin.productHistory.nothing')}</em></div>
                                                                )}
                                                                {d.type === 'modified' && (
                                                                    <div className="text-warning">{formatDiffItem(d.oldItem)} → {formatDiffItem(d.newItem)}</div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div>
                                                            <span className="text-muted">{t('admin.productHistory.oldValue')}:</span> {formatValue(change.oldValue)}
                                                        </div>
                                                        <div>
                                                            <span className="text-muted">{t('admin.productHistory.newValue')}:</span> {formatValue(change.newValue)}
                                                        </div>
                                                    </>
                                                )}
                                            </li>
                                        )
                                    })}
                                </ul>
                            </Col>

                        </Row>
                    )}
                </Modal.Body>
            </Modal>
        </>
    )
}
